import { normalizeArtifactRef } from 'flare-chat-core/contracts/context/artifact-ref';
import { citationCoordinate, isPreparedCitation } from '../timeline/citationBindings.js';
import { selectCanvasArtifactTab } from './artifactBindingProjection.js';

const array = value => Array.isArray(value) ? value : [];
const text = value => typeof value === 'string' && value.trim().length > 0;
const scopeMatches = (left, right) => ['tenant_id', 'project_id'].every(key => text(left?.[key]) && left[key] === right?.[key]);
const citationsFrom = items => array(items).flatMap(item => array(item.citationPayload?.citations)
  .concat(item.type === 'knowledge_citation' ? array(item.payload?.citations) : []));
const uniqueCitations = items => [...new Map(citationsFrom(items).filter(isPreparedCitation)
  .map(citation => [JSON.stringify([citationCoordinate(citation.citation_target), citation.citation_id, citation.citation_anchor_id]), citation])).values()];

/** Join read-only interpretations by the entire saved target, never just citation/source ID. */
export const bindEvidenceReadings = (rows, readings) => {
  const index = array(readings).reduce((map, reading) => {
    const key = citationCoordinate(reading?.target);
    if (key) map.set(key, [...(map.get(key) ?? []), reading]);
    return map;
  }, new Map());
  return rows.map(row => {
    const matches = index.get(citationCoordinate(row.citation_target)) ?? [];
    if (matches.length !== 1) return row;
    return { ...row, reading: { ...row.reading, ...matches[0] } };
  });
};

export const projectSourceReadings = (targets, timelineItems) => {
  const coordinates = new Set(array(targets).filter(target => target?.source_ref).map(citationCoordinate).filter(Boolean));
  return uniqueCitations(timelineItems).filter(citation => coordinates.has(citationCoordinate(citation.citation_target)))
    .map(citation => ({ ...citation, key: citation.citation_anchor_id, evidence_kind: 'retrieval',
      run_id: citation.citation_target.run_id, source_id: citation.citation_target.source_id,
      source_type: citation.source_type ?? 'local', snippet: citation.snippet ?? citation.reading.original_text }));
};

/** Translate the published retrieval_result DTO only; no admission, scoring or fallback reconstruction. */
export const projectSavedRetrievalEvidence = (document, timelineItems) => {
  const body = document?.latest_revision ?? document;
  try {
    const ref = normalizeArtifactRef(body?.artifact_ref);
    if (ref.artifact_type !== 'retrieval_result' || body.artifact_schema !== 'retrieval_result' || body.schema_version !== 1
      || body.artifact_type !== ref.artifact_type || body.artifact_id !== ref.artifact_id
      || !scopeMatches(body.scope, ref.scope) || !['completed', 'degraded'].includes(body.result_status)
      || body.execution?.status !== body.result_status) return [];
    const bindings = uniqueCitations(timelineItems);
    return array(body.results).filter(record => record?.evidence_admission?.state === 'admitted'
      && scopeMatches(record, ref.scope) && scopeMatches(record.scope, ref.scope)
      && [record.source_id, record.evidence_id].every(text))
      .map(record => {
        const target = { artifact_ref: ref, run_id: body.run_id, source_id: record.source_id,
          record_id: record.record_id, chunk_id: record.chunk_id, evidence_id: record.evidence_id };
        const coordinate = citationCoordinate(target);
        const matches = bindings.filter(citation => citationCoordinate(citation.citation_target) === coordinate);
        const citation = matches.length === 1 ? matches[0] : null;
        return { ...record, key: coordinate, evidence_kind: 'retrieval', artifact_ref: ref, run_id: body.run_id,
          source_type: record.source_type ?? record.resource_ref?.source_type ?? 'local',
          query: array(body.parameters?.queries).join(' · '), source_meta: body.execution,
          citation_id: citation?.citation_id, citation_target: target, citation_anchor_id: citation?.citation_anchor_id,
          reading: citation?.reading };
      }).filter(row => row.key);
  } catch { return []; }
};

/** Resolve source before row construction; selected empty versions never borrow timeline results. */
export const resolveRetrievalEvidenceInput = options => {
  const tab = selectCanvasArtifactTab(options);
  const readingTargets = tab?.metadata?.reading_targets;
  if (!tab?.metadata?.artifact_ref && Array.isArray(readingTargets)) return {
    artifactDocument: null, timelineItems: options.timelineItems, readingTargets, evidenceTabKey: 'retrieval',
  };
  if (tab?.metadata?.artifact_ref?.artifact_type !== 'retrieval_result') return options.baseInput;
  return { artifactDocument: options.artifactDocument, timelineItems: options.timelineItems, readingTargets, evidenceTabKey: 'retrieval' };
};

/** Dispatch only an unambiguous target already present in this prepared timeline and canonical tabs. */
export const resolveCitationNavigation = (action, timelineItems, projection) => {
  const coordinate = action.citation_target ? citationCoordinate(action.citation_target) : '';
  const matches = uniqueCitations(timelineItems).filter(citation => citation.citation_id === action.citation_id
    && (!action.citation_target || citationCoordinate(citation.citation_target) === coordinate)
    && (!action.run_id || citation.citation_target.run_id === action.run_id));
  if (matches.length !== 1) return null;
  const citation = matches[0];
  const ref = citation.citation_target.artifact_ref;
  if (ref) return { artifact_target: { ...ref, project_id: ref.scope.project_id }, anchor: citation.citation_anchor_id };
  const tabs = array(projection?.tabs).filter(tab => tab.readiness === 'ready'
    && array(tab.metadata?.reading_targets).some(target => citationCoordinate(target) === citationCoordinate(citation.citation_target)));
  return tabs.length === 1 ? { activeTab: tabs[0].key, anchor: citation.citation_anchor_id } : null;
};
